import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { combineLatest, Observable, of } from "rxjs";
import { first, map, switchMap, tap } from "rxjs/operators";
import {
  AbstractUserService,
  ICirculationTemplate,
  ICirculationTemplateStep,
  IDefaultStepPayloadData,
  IPerson,
  IUser
} from "src/app/interfaces";
import { NavigationService } from "src/app/services";
import { PersonFacade } from "src/app/services/person.facade";
import { TemplateFacade } from "src/app/services/template.facade";
import { LogClass, Logger } from "../../../../logger";
import { STEPTYPE_MAIL } from "../../constants";

@Component({
    selector: "circ-create-template",
    templateUrl: "./template.component.html",
    styleUrls: ["./template.component.scss"],
    standalone: false
})
@LogClass({ source: "stepMail/TemplateComponent" })
export class TemplateComponent implements OnInit {
  private logger: Logger;

  public form: FormGroup;
  public saving = false;

  /** True if updating a existing step */
  public existingStep = false;

  constructor(
    private templateFacade: TemplateFacade,
    private navigate: NavigationService,
    private userService: AbstractUserService,
    private personFacade: PersonFacade
  ) {
    this.logger.debug("ctor");
    this.createForm();
  }
  ngOnInit(): void {
    this.logger.info("init");
    this.templateFacade
      .getCurrentTemplate()
      .pipe(
        tap((t) => {
          if (!t) {
            throw new Error("no Template given");
          }
        }),
        switchMap((t) => this.templateFacade.getCurrentStep(t)),
        tap((step) => {
          const data = step.payload.data as IDefaultStepPayloadData;

          this.form.setValue({
            title: data?.title,
            comment: data?.comment,
            responsible: []
          });
        }),
        switchMap((step) =>
          this.personFacade.getPersonByLogins(
            step.tasks.assignedTo.map((x) => x.login)
          )
        ),
        first()
      )
      .subscribe((persons: IPerson[]) => {
        this.form.patchValue({
          responsible: persons
        });
        this.existingStep = true;
      });
  }

  public abort(): void {
    this.navigateToCurrentTemplate();
  }

  private navigateToCurrentTemplate(): void {
    this.templateFacade
      .getCurrentTemplate()
      .pipe(
        map((item) => item.id),
        first()
      )
      .subscribe((id) => this.navigate.toTemplate(id));
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.saving = true;
    this.logger.info("saving");

    const logins: string[] = this.form.value.responsible.map(
      (x: IPerson) => x.login
    );
    const user$ = this.userService.getUsers(logins);
    const template$ = this.templateFacade.getCurrentTemplate();

    combineLatest([template$, user$])
      .pipe(
        switchMap(([template, users]) =>
          this.existingStep
            ? this.updateStep(template, users)
            : this.addNewStep(template, users)
        ),
        switchMap((item) => this.templateFacade.save(item)),
        first()
      )
      .subscribe((ok) => {
        this.logger.info("saved:" + ok);

        this.navigateToCurrentTemplate();
      });
  }

  private updateStep(
    template: ICirculationTemplate,
    users: IUser[]
  ): Observable<ICirculationTemplate> {
    return of(template).pipe(
      switchMap((t) => this.templateFacade.getCurrentStep(t)),
      map((step) => {
        const data = (step.payload.data || {}) as IDefaultStepPayloadData;
        data.title = this.form.value.title;
        data.comment = this.form.value.comment;
        step.payload.data = data;
        step.duration = null;
        return step;
      }),
      map((step) => {
        step.tasks = {
          assignedTo: users,
          firstResponseApplies: false
        };
        return step;
      }),
      map((step) => {
        template.steps[step.stepNo - 1] = step;
        return template;
      })
    );
  }

  private addNewStep(
    template: ICirculationTemplate,
    users: IUser[]
  ): Observable<ICirculationTemplate> {
    const step: ICirculationTemplateStep = {
      stepNo: template.steps.length + 1,
      tasks: {
        firstResponseApplies: false,
        assignedTo: users
      },
      payload: {
        stepType: STEPTYPE_MAIL,
        data: {
          title: this.form.value.title,
          comment: this.form.value.comment
        }
      }
    };

    template.steps.push(step);
    return of(template);
  }

  private createForm() {
    this.form = new FormGroup({
      title: new FormControl("", [Validators.required]),
      responsible: new FormControl([]),
      comment: new FormControl("", [Validators.required])
    });
  }
}
